import React from 'react';
import { X, CheckCircle, AlertCircle, XCircle, Users, FileSpreadsheet } from 'lucide-react';

const UploadResultsModal = ({ results, onClose }) => {
  if (!results) return null;

  const {
    totalGroupsCreated,
    totalContactsCreated,
    totalContactsAlreadyExists,
    totalContactsWithErrors,
    totalContactsProcessed,
    groups
  } = results;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'CREATED':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'ALREADY_EXISTS':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'ERROR':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'CREATED':
        return 'text-green-700 bg-green-50 border-green-200';
      case 'ALREADY_EXISTS':
        return 'text-yellow-700 bg-yellow-50 border-yellow-200';
      case 'ERROR':
        return 'text-red-700 bg-red-50 border-red-200';
      default:
        return 'text-gray-700 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <FileSpreadsheet className="h-5 w-5 mr-2 text-blue-500" />
                Upload Results
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Summary Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center">
                  <Users className="h-8 w-8 text-blue-500 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-blue-700">Total Processed</p>
                    <p className="text-2xl font-bold text-blue-900">{totalContactsProcessed}</p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center">
                  <CheckCircle className="h-8 w-8 text-green-500 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-green-700">Created</p>
                    <p className="text-2xl font-bold text-green-900">{totalContactsCreated}</p>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center">
                  <AlertCircle className="h-8 w-8 text-yellow-500 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-yellow-700">Already Exists</p>
                    <p className="text-2xl font-bold text-yellow-900">{totalContactsAlreadyExists}</p>
                  </div>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <XCircle className="h-8 w-8 text-red-500 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-red-700">Errors</p>
                    <p className="text-2xl font-bold text-red-900">{totalContactsWithErrors}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Groups Summary */}
            <div className="mb-6">
              <h4 className="text-md font-medium text-gray-900 mb-3">
                Groups Created: {totalGroupsCreated}
              </h4>
              <div className="space-y-3">
                {groups && groups.map((group, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-medium text-gray-900">{group.groupName}</h5>
                      <div className="flex space-x-4 text-sm">
                        <span className="text-green-600">✓ {group.contactsCreated} created</span>
                        <span className="text-yellow-600">⚠ {group.contactsAlreadyExists} exists</span>
                        <span className="text-red-600">✗ {group.contactsWithErrors} errors</span>
                      </div>
                    </div>
                    
                    {/* Show first few contacts for each group */}
                    {group.contacts && group.contacts.length > 0 && (
                      <div className="mt-3">
                        <div className="text-sm text-gray-600 mb-2">
                          Sample contacts ({Math.min(5, group.contacts.length)} of {group.contacts.length}):
                        </div>
                        <div className="space-y-1">
                          {group.contacts.slice(0, 5).map((contact, contactIndex) => (
                            <div
                              key={contactIndex}
                              className={`flex items-center justify-between p-2 rounded border ${getStatusColor(contact.status)}`}
                            >
                              <div className="flex items-center">
                                {getStatusIcon(contact.status)}
                                <span className="ml-2 font-medium">{contact.contactName}</span>
                                <span className="ml-2 text-sm opacity-75">{contact.phoneNumber}</span>
                              </div>
                              <span className="text-xs font-medium uppercase">{contact.status}</span>
                            </div>
                          ))}
                          {group.contacts.length > 5 && (
                            <div className="text-sm text-gray-500 text-center py-2">
                              ... and {group.contacts.length - 5} more contacts
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end pt-4 border-t border-gray-200">
              <button
                onClick={onClose}
                className="btn-primary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadResultsModal;
